import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import {
  Onboarding,
  OnboardingContext,
  OnboardingTitle,
} from 'registration/containers/Onboarding';

import { Checkbox, STYLE_TYPE } from 'shared/components/ihcl/checkbox';
import { Center } from 'shared/components/ihcl/positioning';
import { Select } from 'shared/components/ihcl/select';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';

const DEFAULT_ONBOARDING_TITLE_TEXT =
  'Where do you have an active nursing license?';

const StateLicenseRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
});

const StateRows = styled('ul', () => ({
  listStyleType: 'none',
  paddingLeft: 0,
}));

const StateRow = styled('li', ({ $theme }) => ({
  borderBottomColor: $theme.colors.grayLine,
  borderBottomStyle: 'solid',
  borderBottomWidth: '1px',
  paddingTop: '20px',
  paddingBottom: '20px',
}));

export const NoLicense = styled('div', {
  marginTop: '1em',
  marginBottom: '1em',
});

const DefaultStateAction = ({ state, licenseStates, setLicenseStates }) => (
  <Checkbox
    ariaLabel={`${state.name}`}
    checked={state.hasActiveLicense}
    checkmarkType={STYLE_TYPE.toggle_round}
    onChange={(e) => {
      setLicenseStates(
        licenseStates.map((licenseState) => {
          if (state.abbreviation === licenseState.abbreviation) {
            return {
              ...licenseState,
              hasActiveLicense: e.target.checked,
            };
          }
          return licenseState;
        })
      );
    }}
  />
);

DefaultStateAction.propTypes = {
  licenseStates: PropTypes.arrayOf(
    PropTypes.shape({
      abbreviation: PropTypes.string.isRequired,
    })
  ).isRequired,
  setLicenseStates: PropTypes.func.isRequired,
  state: PropTypes.shape({
    abbreviation: PropTypes.string,
    hasActiveLicense: PropTypes.bool,
    name: PropTypes.string,
  }).isRequired,
};

const DefaultCompactAction = ({
  children,
  state,
  licenseStates,
  setLicenseStates,
}) => (
  <Checkbox
    checked={state.hasCompactLicense}
    onChange={(e) => {
      setLicenseStates(
        licenseStates.map((licenseState) => {
          if (state.abbreviation === licenseState.abbreviation) {
            return {
              ...licenseState,
              hasCompactLicense: e.target.checked,
            };
          }
          return licenseState;
        })
      );
    }}
  >
    {children}
  </Checkbox>
);

DefaultCompactAction.propTypes = {
  children: PropTypes.node.isRequired,
  licenseStates: PropTypes.arrayOf(
    PropTypes.shape({
      abbreviation: PropTypes.string.isRequired,
    })
  ).isRequired,
  setLicenseStates: PropTypes.func.isRequired,
  state: PropTypes.shape({
    abbreviation: PropTypes.string.isRequired,
    hasActiveLicense: PropTypes.bool,
    hasCompactLicense: PropTypes.bool,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

type ActiveLicensesProps = {
  CompactAction?: React.ElementType;
  licenseStates: any[];
  setLicenseStates: Function;
  StateAction?: React.ElementType;
  stateOptions: any[];
};
export const ActiveLicenses = ({
  CompactAction = DefaultCompactAction,
  licenseStates,
  setLicenseStates,
  StateAction = DefaultStateAction,
  stateOptions,
}: ActiveLicensesProps) => {
  const [isAddingState, setIsAddingState] = useState(false);

  const selectOverrides = {
    Popover: {
      props: {
        overrides: {
          Body: {
            style: {
              zIndex: 1201, // 1 higher than TalentModal zIndex
            },
          },
        },
      },
    },
  };

  return (
    <div>
      <StateRows>
        {licenseStates.map((state) => (
          <StateRow key={state.id || state.state_id}>
            <StateLicenseRow>
              {state.name || state.label}
              <StateAction
                state={state}
                licenseStates={licenseStates}
                setLicenseStates={setLicenseStates}
              />
            </StateLicenseRow>
            {state.compact && state.hasActiveLicense && (
              <CompactAction
                state={state}
                licenseStates={licenseStates}
                setLicenseStates={setLicenseStates}
              >
                This is a compact license (eNLC)
              </CompactAction>
            )}
          </StateRow>
        ))}
      </StateRows>
      <Center>
        {!isAddingState && (
          <Button variant="minimal" onClick={() => setIsAddingState(true)}>
            + Add another state
          </Button>
        )}
        {isAddingState && (
          <Select
            instanceId="stateLicenseOptions"
            autoFocus
            startOpen
            options={stateOptions}
            onChange={({ option }) => {
              if (option && option.id) {
                if (
                  !licenseStates.some(
                    (licenseState) => licenseState.id === option.id
                  )
                ) {
                  setLicenseStates([
                    ...licenseStates,
                    {
                      ...option,
                      hasActiveLicense: true,
                    },
                  ]);
                }
                setIsAddingState(false);
              }
            }}
            overrides={selectOverrides}
          />
        )}
      </Center>
    </div>
  );
};

type OnboardingLicensesProps = {
  nextAction?: string;
  previousAction?: string;
  stepNumber: number;
};
const OnboardingLicenses = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
}: OnboardingLicensesProps) => {
  // FIXME: Context should work without casting to any
  const {
    onboardingData,
    onboardingOptions,
    onboardingUpdates,
    setOnboardingData,
  }: any = useContext(OnboardingContext);
  const {
    currentLocation,
    desiredLocations,
    license_being_processed: licenseBeingProcessed,
  } = onboardingData;
  const { other_locations_attributes: otherLocationsAttributes } =
    onboardingUpdates;
  const locations = [
    currentLocation,
    ...(otherLocationsAttributes || []),
    ...(desiredLocations || []),
  ];
  const statesToCheck = Object.keys(
    locations.reduce((statesMap, location) => {
      const states = { ...statesMap };
      if (location && location.state) {
        states[location.state] = true;
      }
      return states;
    }, {})
  );
  const initialLicenseStates = onboardingData.state_licenses || [];
  onboardingOptions.states
    .filter((state) => statesToCheck.includes(state.abbreviation))
    .forEach((state) => {
      if (
        !initialLicenseStates.find(
          (licenseState) => licenseState.id === state.id
        )
      ) {
        initialLicenseStates.push(state);
      }
    });
  initialLicenseStates.sort((a, b) => a.name?.localeCompare(b.name));

  const stateLicencesAttributes =
    onboardingData.state_licenses_attributes || [];

  const getExistingCompactLicense = (initialLicenseState, matchingAttr) => {
    if (initialLicenseState.hasCompactLicense) {
      return { hasCompactLicense: initialLicenseState.hasCompactLicense };
    }

    if (matchingAttr && matchingAttr.compact) {
      return { hasCompactLicense: matchingAttr.compact };
    }

    return {};
  };

  const updatedStateLicenses = initialLicenseStates.map(
    (initialLicenseState) => {
      const matchingAttr = stateLicencesAttributes.find(
        (attr) => attr.state_id === initialLicenseState.id
      );

      return {
        ...initialLicenseState,
        hasActiveLicense:
          initialLicenseState.hasActiveLicense || !!matchingAttr,
        ...getExistingCompactLicense(initialLicenseState, matchingAttr),
      };
    }
  );

  const [noActiveLicense, setNoActiveLicense] = useState(false);
  const [licenseStates, setLicenseStates] = useState(updatedStateLicenses);
  const setStateLicenses = (newLicenseStates) => {
    if (
      newLicenseStates.some((licenseState) => licenseState.hasActiveLicense) &&
      noActiveLicense
    ) {
      setNoActiveLicense(false);
    }
    setLicenseStates(newLicenseStates);
    setOnboardingData({
      ...onboardingData,
      state_licenses_attributes: newLicenseStates
        .filter((licenseState) => licenseState.hasActiveLicense)
        .map((licenseState) => ({
          state_id: licenseState.id,
          compact: licenseState.compact
            ? !!licenseState.hasCompactLicense
            : undefined,
        })),
    });
  };

  let onboardingTitleText = DEFAULT_ONBOARDING_TITLE_TEXT;
  if (onboardingData.signup_intent === 'resume') {
    onboardingTitleText =
      '🎉 Last one! Where do you have an active nursing license?';
  }
  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={
        !licenseStates.some((licenseState) => licenseState.hasActiveLicense) &&
        !noActiveLicense
      }
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['state_licenses_attributes', 'license_being_processed']}
      stepName="State licenses"
    >
      <OnboardingTitle>{onboardingTitleText}</OnboardingTitle>
      <ActiveLicenses
        licenseStates={licenseStates}
        setLicenseStates={setStateLicenses}
        stateOptions={onboardingOptions.states.map((state) => ({
          ...state,
          disabled: licenseStates.some(
            (licenseState) => licenseState.id === state.id
          ),
          label: state.name,
        }))}
      />
      <NoLicense id="no-active-license-wrapper">
        <Checkbox
          checked={noActiveLicense}
          onChange={(e) => {
            if (e.target.checked) {
              setStateLicenses(
                licenseStates.map((licenseState) => ({
                  ...licenseState,
                  hasActiveLicense: false,
                }))
              );
            }
            setNoActiveLicense(e.target.checked);
          }}
        >
          I don&apos;t have an active license
        </Checkbox>
        <Checkbox
          checked={licenseBeingProcessed}
          onChange={(e) => {
            setOnboardingData({
              ...onboardingData,
              license_being_processed: e.target.checked,
            });
          }}
        >
          My license is being processed
        </Checkbox>
      </NoLicense>
    </Onboarding>
  );
};

export default OnboardingLicenses;
